import React, { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';

import { StatsSection } from '../../../statspro/js/components/common/stats-section';

import {
    RoundSelectNavItem,
    CompNavItem,
    ResponsiveSeasonNavItem,
    ResponsiveRoundNavItem,
    LiveToggleNavItem
} from 'common/react/components/competition-nav/nav-items';

import { navSelector } from 'store/modules/competition-nav/selectors/nav-selector';
import { useCompetitionNavigationOptions } from 'common/react/hooks/use-competition-navigation-options';
import { useWindowSize } from 'common/react/hooks/use-window-resize';
import { useGetCurrentCompFilters } from 'common/react/components/competition-nav/hooks/use-get-current-comp-filters';
import { useFiltersTracking } from 'common/react/hooks/use-filters-tracking';
import { FilterDropdown } from 'widgets/filters/js/components/FilterDropdown';
import { FilterButton } from 'common/react/components/FilterButton';
import { getTranslation } from 'common/react/utils/translations';
import { initialiseNav } from 'common/store/modules/competition-nav/thunks';
import { useGetUrlParams } from 'common/react/components/competition-nav/hooks/use-get-url-params';
import { useSyncUrlParams } from 'common/react/hooks/use-sync-url-params';

/**
 * @constant {Array} Competitions we want to appear in the filter for the ladder
 */
export const ALLOWED_COMPS = globalThis.LADDER_DEFAULT_COMP_ORDER
    ? globalThis.LADDER_DEFAULT_COMP_ORDER.split(',')
    : [
          PULSE.app.common.CONSTANTS.PROVIDER_IDS.WOMENS,
          PULSE.app.common.CONSTANTS.PROVIDER_IDS.PREMIERSHIP,
          PULSE.app.common.CONSTANTS.PROVIDER_IDS.VFL_PREMIERSHIP,
          PULSE.app.common.CONSTANTS.PROVIDER_IDS.VFL_WOMENS,
          PULSE.app.common.CONSTANTS.PROVIDER_IDS.SANFL_PREMIERSHIP
      ];

export const LadderNav = ({ competitionId, seasonId, title }) => {
    const windowSize = useWindowSize();
    const dispatch = useDispatch();

    const isResponsive = () =>
        windowSize.width < PULSE.app.measurements.desktop;
    const [showResponsiveItems, setShowResponsiveItems] = useState(
        isResponsive()
    );
    const [areFilterOptionsVisible, setAreFilterOptionsVisible] =
        useState(false);

    useEffect(() => {
        setShowResponsiveItems(
            windowSize.width < PULSE.app.measurements.desktop
        );
    }, [windowSize.width]);

    const params = useGetUrlParams();

    const initialValues = {
        competitionId: params.competition || competitionId,
        seasonId: params.season || seasonId,
        roundId: params.round || ''
    };

    useEffect(() => {
        dispatch(
            initialiseNav({ initialValues: initialValues, removeFinals: true })
        );
    }, [dispatch]);

    useSyncUrlParams({
        selector: navSelector,
        mapToParams: (items) => ({
            Competition: items.competition.id,
            Season: items.season.id,
            Round: items.round.id
        })
    });

    const nav = useSelector(navSelector);

    const options = useCompetitionNavigationOptions(nav);

    const { competitions, seasons } = options;

    const currentFilters = useGetCurrentCompFilters();
    const competitionNavRef = useRef();

    const resetToDefaults = () => {
        const initialCompetitionId = Number.parseInt(competitionId) || '';
        const initialSeasonId = Number.parseInt(seasonId) || '';
        const initialRoundId = nav?.round?.initialItemId || '';

        if (
            nav?.competition?.id !== initialCompetitionId ||
            nav?.season?.id !== initialSeasonId ||
            nav?.round?.id !== initialRoundId
        ) {
            dispatch(
                initialiseNav({
                    initialValues: {
                        competitionId,
                        seasonId,
                        roundId: initialRoundId
                    },
                    removeFinals: true
                })
            );
        }
    };

    const hasRoundChanged = useCallback(() => {
        if (!nav?.round) {
            return false;
        }

        return nav?.round?.id !== nav?.round?.initialItemId;
    }, [nav?.round]);

    const isSeasonChanged = useCallback(() => {
        if (!nav?.season) {
            return false;
        }

        return (
            Number.parseInt(nav?.season?.id, 10) !==
            Number.parseInt(seasonId, 10)
        );
    }, [nav?.season]);

    const hasChangedFromDefaults = useCallback(() => {
        if (!nav?.competition || !nav?.season || !nav?.round) {
            return false;
        }
        const isCompetitionChanged =
            nav?.competition?.id !== nav?.competition?.initialItemId;
        return isCompetitionChanged || isSeasonChanged() || hasRoundChanged();
    }, [nav?.competition, nav?.season, nav?.round]);

    /**
     * custom hook providing tracking of filters
     */
    useFiltersTracking({
        title,
        filtersState: nav,
        options,
        filtersFields: ['competition', 'season', 'round'],
        filtersRef: competitionNavRef
    });

    const renderDesktop = () => (
        <StatsSection.ScrollableItems>
            <CompNavItem
                classes="fixtures-nav__comp-select"
                competitions={competitions}
                allowListOrder={ALLOWED_COMPS}
                hidePreseason={true}
            />
            <FilterDropdown
                title={title}
                icon="season-trophy"
                navItem="season"
                items={seasons}
                removeFinals
            />
            <RoundSelectNavItem removeFinals className={''} />
            {hasChangedFromDefaults() ? (
                <div className="stats-leaders-nav__item-wrapper stats-leaders-nav__item-wrapper--reset">
                    <button
                        className="stats-leaders-filter-reset-button"
                        onClick={resetToDefaults}
                    >
                        {getTranslation(
                            'label.statsLeaders.filters.resetButtonText'
                        )}
                    </button>
                </div>
            ) : null}

            {!showResponsiveItems && currentFilters?.round?.providerId ? (
                <LiveToggleNavItem
                    roundId={currentFilters.round.providerId}
                    className="competition-nav__live-toggle-wrapper competition-nav__live-toggle-wrapper--ladder"
                />
            ) : null}
        </StatsSection.ScrollableItems>
    );

    const renderMobile = () => (
        <>
            <StatsSection.NavItems>
                <CompNavItem
                    classes="fixtures-nav__comp-select"
                    competitions={competitions}
                    allowListOrder={ALLOWED_COMPS}
                    hidePreseason
                    hideLabel
                    removeFinals
                />
                <FilterButton
                    classes="fixtures-nav__filter-button"
                    onChange={() => setAreFilterOptionsVisible((prev) => !prev)}
                    areFilterOptionsVisible={areFilterOptionsVisible}
                    showFilterLabel={false}
                    showActiveCircle
                    isFilterSelectedCustom={() =>
                        isSeasonChanged() || hasRoundChanged()
                    }
                />
            </StatsSection.NavItems>
            {areFilterOptionsVisible ? (
                <StatsSection.NavFilterItems
                    initialValues={{
                        competitionId,
                        seasonId
                    }}
                    onReset={resetToDefaults}
                >
                    <ResponsiveSeasonNavItem seasons={seasons} removeFinals />
                    <ResponsiveRoundNavItem removeFinals={true} />
                    {hasChangedFromDefaults() ? (
                        <div className="stats-leaders-nav__item-wrapper stats-leaders-nav__item-wrapper--reset">
                            <button
                                className="stats-leaders-filter-reset-button"
                                onClick={resetToDefaults}
                            >
                                {getTranslation(
                                    'label.statsLeaders.filters.resetButtonText'
                                )}
                            </button>
                        </div>
                    ) : null}
                </StatsSection.NavFilterItems>
            ) : null}
            {showResponsiveItems && currentFilters?.round?.providerId && (
                <StatsSection.ResponsiveRowItems>
                    <LiveToggleNavItem
                        roundId={currentFilters.round.providerId}
                        className="competition-nav__live-toggle-wrapper competition-nav__live-toggle-wrapper--ladder"
                        labelRightPosition
                    />
                </StatsSection.ResponsiveRowItems>
            )}
        </>
    );

    return (
        <div
            className="competition-nav filter-dropdown"
            ref={competitionNavRef}
        >
            <StatsSection.Nav
                withScrollableFilter={!showResponsiveItems}
                useRefinedFilter
            >
                {showResponsiveItems ? renderMobile() : renderDesktop()}
            </StatsSection.Nav>
        </div>
    );
};

LadderNav.propTypes = {
    competitionId: PropTypes.string,
    seasonId: PropTypes.string,
    title: PropTypes.string
};
